<template lang="pug">
.event-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    // セル：公開
    template(#cell(publishing)="row")
      PublicSwitch.mx-auto(:id="'switch-' + row.item.id" :value="row.item.isPublic" @onSwitch="onStatusSwitch(row.item)")
    // セル：日付
    template(#cell(date)="row")
      .date
        span {{ row.item.datetime.date }}
        span （
        span(:class="weekCssClass(row.item.datetime.day)") {{ row.item.datetime.day }}
        span ）
      .time
        span {{ row.item.datetime.timeFrom }}～{{ row.item.datetime.timeTo }}
    // セル：公開先
    template(#cell(targets)="row")
      .targets
        .target(v-for="target in row.item.targets") {{ target }}
    // セル：内容
    template(#cell(preview)="row")
      b-spinner.spinner(v-if="row.item.imageLoading == true")
      img(v-else-if="row.item.convertedImageUrl" @click="onPreview(row.item)" :src="row.item.convertedImageUrl")
      img(v-else @click="onPreview(row.item)" :src="defaultImage")
    // セル：申込み状況
    template(#cell(status)="row")
      button.btn.status-btn.disable(v-if="row.item.needReply == false" disabled) 確認
      .status-content(v-else)
        b-spinner.spinner(v-if="row.item.attendeeCount == undefined")
        span(v-else) {{ row.item.attendeeCount }} 件
        button.btn.status-btn.ml-2(@click="onApplicationStatus(row.item)") 確認
    // セル：ライブ配信
    template(#cell(live)="row")
      button.btn.live-start-btn.disable(v-if="row.item.holdType == 1 || row.item.meetingToken == ''" disabled) 配信開始
      button.btn.live-start-btn(v-else @click="onLiveStart(row.item)") 配信開始
    // セル：登録情報
    template(#cell(operation)="row")
      button.btn.edit-btn.disable(v-if="row.item.isPublic" type="button" disabled) 修正
      button.btn.edit-btn(v-else @click="onEdit(row.item)" type="button") 修正
      button.btn.delete-btn.disable(v-if="row.item.isPublic" type="button" disabled) 削除
      button.btn.delete-btn(v-else @click="onDelete(row.item)" type="button") 削除
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import { EventHoldType } from "@/commons/services/Events";

import PublicSwitch from "@/pc/basics/PublicSwitch.vue";

/**
 * ### イベントテーブルコンポーネント
 */
@Component({
  components: {
    PublicSwitch
  }
})
export default class EventTable extends Vue {
  /** 行情報 */
  @Prop({ required: true })
  value!: Array<EventTableItem>;
  /** 列名 */
  @Prop({ required: true })
  fields!: Array<EventTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** 読込み中か否か */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: FilterSetting;

  get _value(): Array<EventTableItem> {
    return this.value;
  }

  /**
   * ### 公開/非公開のスイッチ押下
   */
  public onStatusSwitch(item: EventTableItem): void {
    this.$emit("onStatusSwitch", item);
    return;
  }

  /**
   * ### 修正ボタン押下
   */
  public onEdit(item: EventTableItem): void {
    this.$emit("onEdit", item);
    return;
  }

  /**
   * ### 削除ボタン押下
   */
  public onDelete(item: EventTableItem): void {
    this.$emit("onDelete", item);
    return;
  }

  /**
   * ### 画像表示ボタン押下
   */
  public onPreview(item: EventTableItem): void {
    this.$emit("onPreview", item);
    return;
  }

  /**
   * ### 申込状況確認ボタン押下
   */
  public onApplicationStatus(item: EventTableItem): void {
    this.$emit("onApplicationStatus", item);
    return;
  }

  /**
   * ### 配信開始ボタン押下
   */
  public onLiveStart(item: EventTableItem): void {
    this.$emit("onLiveStart", item);
    return;
  }

  /**
   * ### 曜日の文字色CssClassの割り当て
   */
  public weekCssClass(weekName: string): string {
    let weekClassName: string = "";
    switch (weekName) {
      case "日":
        weekClassName = "sunday";
        break;
      case "土":
        weekClassName = "saturday";
        break;
      default:
        break;
    }
    return weekClassName;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: EventTableItem, filter: FilterSetting): boolean {
    return this.filterSearchWords(row, filter);
  }

  /**
   * ### 検索文字列でフィルタリング
   */
  public filterSearchWords(row: EventTableItem, filter: FilterSetting): boolean {
    if (filter.searchWord == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.searchWord);

    let foundTitle: boolean = true;

    for (const word of words) {
      if (row.title.indexOf(word) == -1) {
        foundTitle = false;
      }
    }

    return foundTitle;
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }
}

/** テーブルの列名の情報 */
export interface EventTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface EventTableItem {
  id: number;
  date: number;
  datetime: {
    date: string;
    day: string;
    timeFrom: string;
    timeTo: string;
  };
  timeFrom: number;
  timeTo: number;
  targets: string[];
  place: string;
  isPublic: boolean;
  title: string;
  description: string;
  imageUrl: string;
  convertedImageUrl: string;
  imageLoading: boolean;
  meetingToken: string;
  holdType: EventHoldType;
  needReply: boolean;
  replyDeadline: number;
  attendeeCount?: number;
  writtenBy: string;
  writerId: string;
  updated: number;
}

export interface FilterSetting {
  searchWord: string;
}
</script>

<style lang="scss" scoped>
.event-table-component {
  .edit-btn,
  .delete-btn,
  .status-btn {
    width: 64px;
  }
  .edit-btn {
    margin-right: 12px;
  }

  .preview {
    img {
      width: 106px;
      border: 1px #bbb solid;
      cursor: pointer;
    }
  }
}
</style>
